import { EdgerRoll } from "@/pages/catalogs/models/EdgerRoll";
import { RollPositionTag } from "@/pages/catalogs/shared/components/RollPositionTag";
import { RollStatusTag } from "@/pages/catalogs/shared/components/RollStatusTag";
import { RollPassStatus } from "@/shared/selectors/RollPassStatus";
import { rollPositionCodes } from "@/shared/selectors/RollPositionSelector";
import { rollStatusCodes } from "@/shared/selectors/RollStatusSelector";
import { ErrorModal } from "@dexteel/mesf-core";
import { Grid2, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Part } from "../../models/Part";
import { getEdgerRolls } from "../../repositories/parts/EdgerRollRepository";

type useRollsProps = {
	partId: number;
	onError: (error: Error) => void;
	onSuccess?: (data: EdgerRoll[]) => void;
};

export const useRolls = ({ partId, onError, onSuccess }: useRollsProps) => {
	return useQuery<EdgerRoll[], Error>({
		queryKey: ["edger-rolls", partId],
		queryFn: ({ signal }) => getEdgerRolls({ partId }, signal),
		onError: onError,
		onSuccess: onSuccess,
	});
};

type Props = {
	part: Part | null;
};

export const SpecificEdgerRollData = ({ part }: Props) => {
	const [error, setError] = useState("");
	const [roll, setRoll] = useState<EdgerRoll>();

	const { data: rolls } = useRolls({
		partId: part?.PartId!,
		onError: (error) => setError(error.message),
		onSuccess: (data: EdgerRoll[]) => {
			setRoll(data[0]);
		},
	});

	return (
		<>
			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
					<TextField
						value={roll?.StandName || ""}
						label="Stand"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 4 }} justifyContent="center">
					<RollPassStatus
						passesStatus={roll?.PassesStatus ?? ""}
						justifyContent="center"
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 3 }}>
					<TextField
						value={
							rollPositionCodes.find(
								(status) => status.Code === roll?.RollPosition,
							)?.Name || ""
						}
						label="Position"
						variant="outlined"
						size="small"
						fullWidth
						disabled
						InputProps={{
							startAdornment: (
								<span
									style={{
										width: "25px",
										height: "25px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginRight: "8px",
										marginLeft: "8px",
									}}
								>
									<RollPositionTag
										position={{
											Code:
												rollPositionCodes.find(
													(status) => status.Code === roll?.RollPosition,
												)?.Code || "",
											Name:
												rollPositionCodes.find(
													(status) => status.Code === roll?.RollPosition,
												)?.Name || "",
										}}
									/>
								</span>
							),
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 3 }}>
					<TextField
						value={
							rollStatusCodes.find(
								(status) => status.Code === roll?.RollStatusCode,
							)?.Name || ""
						}
						label="Status"
						variant="outlined"
						size="small"
						fullWidth
						disabled
						InputProps={{
							startAdornment: (
								<span
									style={{
										width: "25px",
										height: "25px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginRight: "8px",
										marginLeft: "8px",
									}}
								>
									<RollStatusTag
										status={{
											Code:
												rollStatusCodes.find(
													(status) => status.Code === roll?.RollStatusCode,
												)?.Code || "",
											Name:
												rollStatusCodes.find(
													(status) => status.Code === roll?.RollStatusCode,
												)?.Name || "",
										}}
									/>
								</span>
							),
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
					<TextField
						value={roll?.NewDiameter || ""}
						label="New Diameter"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
					<TextField
						value={roll?.Diameter || ""}
						label="Diameter"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
					<TextField
						value={roll?.ScrapDiameter || ""}
						label="Scrap Diameter"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
